import {CSSProperties} from "react";

// The site's own accent: the blue of the Tristar logo. A product's own color (Administration > Products) replaces it on
// that product's parts of the public pages.
export const BRAND_ACCENT = '#2f5bff';

// The pages' backgrounds in each color mode (--mk-bg in site.css), which accent-colored text has to be readable on.
const DARK_BACKGROUND = '#0e0f13';
const LIGHT_BACKGROUND = '#ffffff';

type Rgb = [number, number, number];

function parse(hex: string): Rgb | undefined {
    const match = /^#([0-9a-f]{6})$/i.exec(hex.trim());
    if (!match) {
        return undefined;
    }
    const n = parseInt(match[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const toHex = (rgb: Rgb) => '#' + rgb.map(x => Math.round(x).toString(16).padStart(2, '0')).join('');

// WCAG relative luminance and contrast ratio.
function luminance([r, g, b]: Rgb): number {
    const channel = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: string, b: string): number {
    const [la, lb] = [luminance(parse(a)!), luminance(parse(b)!)];
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// The color, moved towards target (white or black) a tenth at a time until it reads at 4.5:1 on background.
function readableOn(rgb: Rgb, background: string, target: number): string {
    let color = toHex(rgb);
    for (let mix = 0.1; contrast(color, background) < 4.5 && mix <= 1; mix += 0.1) {
        color = toHex(rgb.map(c => c + (target - c) * mix) as Rgb);
    }
    return color;
}

/**
 * The colors a product's sections are drawn with, from its accent (the site's when it has none, or one that isn't
 * #rrggbb): the accent itself (buttons, glows, lines), versions of it for text on the page in dark mode (lightened
 * until it reads at 4.5:1) and in light mode (darkened until it does), and the text color on an accent background
 * (near-black or white, whichever reads better).
 */
export function accentColors(accent?: string): { accent: string, inkDark: string, inkLight: string, onAccent: string } {
    const rgb = parse(accent ?? '') ?? parse(BRAND_ACCENT)!;
    const base = toHex(rgb);
    const onAccent = contrast(base, '#111111') >= contrast(base, '#ffffff') ? '#111111' : '#ffffff';
    return { accent: base, inkDark: readableOn(rgb, DARK_BACKGROUND, 255), inkLight: readableOn(rgb, LIGHT_BACKGROUND, 0), onAccent };
}

// The accent as the custom properties site.css draws the public pages with, for the element around a product's parts:
// data-accent has the stylesheet pick the text color for the visitor's color mode.
export function accentProps(accent?: string): { style: CSSProperties, 'data-accent': string } {
    const colors = accentColors(accent);
    return {
        style: {
            '--mk-accent': colors.accent, '--mk-on-accent': colors.onAccent,
            '--mk-accent-ink-dark': colors.inkDark, '--mk-accent-ink-light': colors.inkLight
        } as CSSProperties,
        'data-accent': ''
    };
}
